/**
 * Manages display of multiple layers separated by underlay.
 *
 * @author tku
 */
import { lockUntilEscaped, unlock } from "../../dom/focus-jail";

// Minimal z-index for layers
const Z_INDEX_BASE = 950;

// Stack of layers
const layers = [];

// Lazy underlay node
let underlayNode = null;
function getUnderlayNode() {
	if (underlayNode === null) {
		underlayNode = document.createElement("div");
		underlayNode.classList.add("dialog-underlay");
		underlayNode.addEventListener("click", onUnderlayClick);
		document.body.appendChild(underlayNode);
	}
	return underlayNode;
}

function onUnderlayClick() {
	layers[layers.length - 1].hideCallback();
}

// Returns the next possible z-index value for a layer to be
// on top of all others in stack.
function getNextZIndex() {
	let current = Z_INDEX_BASE;

	// Get z-index of topmost layer (we need to be on top of it)
	if (layers.length > 0) {
		current = parseInt(layers[layers.length - 1].node.style.zIndex);
	}

	// Increase by two to have room for the underlay.
	return current + 2;
}

// Returns the stack index for the given layer node.
function getLayerStackIndex(node) {
	let index = layers.length - 1;
	// Layer is not topmost item in stack
	if (layers[index]?.node != node) {
		index = layers.findIndex(layer => layer.node == node);
	}

	return index;
}

export function addLayer(node, hideCallback, focusFirstTabbable = true) {
	if (getLayerStackIndex(node) != -1) {
		return;
	}

	// Position the underlay right below the layer
	const zIndex = getNextZIndex();
	const underlayNode = getUnderlayNode();
	underlayNode.style.zIndex = zIndex - 1;
	setTimeout(() => {
		underlayNode.classList.add("dialog-underlay--show");
	}, 0);

	layers.push({ node, hideCallback });
	node.style.zIndex = zIndex;

	lockUntilEscaped(node, focusFirstTabbable).then(() => {
		hideCallback();
	});
}

export function removeLayer(node) {
	node.style.zIndex = null;

	const stackIndex = getLayerStackIndex(node);
	if (stackIndex == -1) {
		return;
	}

	// Remove element from stack
	layers.splice(stackIndex, 1);

	// Hide underlay if there are no more layers in stack
	if (layers.length == 0) {
		unlock();
		getUnderlayNode().classList.remove("dialog-underlay--show");
		return;
	}

	// Find the topmost layer in stack and place underlay accordingly
	const topLayer = layers[layers.length - 1];
	const topLayerNode = topLayer.node;
	const underlayNode = getUnderlayNode();
	underlayNode.style.zIndex = topLayerNode.style.zIndex - 1;

	lockUntilEscaped(topLayerNode, false).then(() => {
		topLayer.hideCallback();
	});
}

export function hasLayers() {
	return layers.length > 0;
}
